import { useApolloClient, useQuery } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { GET_SCORING_BATCH } from '@gql/scoring';
import { ScoringBatch } from '../types';

const POLL_INTERVAL_MS = 3000;

export const useGetScoringBatch = (batchId: string | null) => {
    const client = useApolloClient();
    const previousSettledCountRef = useRef<number | null>(null);

    const { data, loading, error, stopPolling, startPolling, refetch } = useQuery<
        { getScoringBatch: ScoringBatch },
        { batchId: string }
    >(GET_SCORING_BATCH, {
        variables: { batchId: batchId! },
        skip: !batchId,
        pollInterval: POLL_INTERVAL_MS,
    });

    const batch = data?.getScoringBatch ?? null;

    useEffect(() => {
        previousSettledCountRef.current = null;
    }, [batchId]);

    useEffect(() => {
        if (!batch) {
            return;
        }

        const settledCount = (batch.done ?? 0) + (batch.error ?? 0);

        if (previousSettledCountRef.current !== null && settledCount > previousSettledCountRef.current) {
            client.cache.evict({ fieldName: 'searchTalents' });
            client.cache.evict({ fieldName: 'searchTalentFilters' });
            client.cache.evict({ fieldName: 'getSearchPresets' });
            client.cache.gc();
        }

        previousSettledCountRef.current = settledCount;
    }, [batch, client]);

    // Stop polling once all items are settled (no pending or in-progress)
    useEffect(() => {
        if (batch && batch.pending === 0 && batch.inProgress === 0) {
            stopPolling();
        }
    }, [batch, stopPolling]);

    return { batch, loading, error, refetch, startPolling, stopPolling };
};
